/**
 * CONTACT MOCK DATA
 *
 * Centralized mock data for contacts including list views and records.
 * Uses mockDataManager for localStorage persistence.
 */

import { createPersistedMockData } from '../mockDataManager';
import type { ListView, ListViewRecord } from '../../types/listView';

/**
 * DEFAULT CONTACT LIST VIEWS
 */
export const defaultContactListViews: ListView[] = [
  {
    id: 'lv_contacts_all',
    name: 'All Contacts',
    apiName: 'all_contacts',
    objectApiName: 'Contact',
    description: 'All contact records',
    isDefault: true,
    columns: [
      { fieldApiName: 'id', fieldLabel: 'Contact ID', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'name', fieldLabel: 'Contact Name', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'title', fieldLabel: 'Title', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'account', fieldLabel: 'Account', fieldType: 'text', isVisible: true, order: 3 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 4 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', isVisible: true, order: 5 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 6 },
    ],
    createdBy: 'System',
    createdDate: '2024-01-01T00:00:00Z'
  },
  {
    id: 'lv_contacts_my',
    name: 'My Contacts',
    apiName: 'my_contacts',
    objectApiName: 'Contact',
    description: 'Contacts I own',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Contact Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'title', fieldLabel: 'Title', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'account', fieldLabel: 'Account', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 3 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', isVisible: true, order: 4 },
      { fieldApiName: 'lastContact', fieldLabel: 'Last Contact', fieldType: 'date', isVisible: true, order: 5 },
    ],
    filters: [
      { fieldApiName: 'owner', operator: 'equals', value: 'Current User' }
    ],
    createdBy: 'Admin',
    createdDate: '2024-02-10T09:00:00Z'
  },
  {
    id: 'lv_contacts_recent',
    name: 'Recently Contacted',
    apiName: 'recent_contacts',
    objectApiName: 'Contact',
    description: 'Contacts contacted in the last 7 days',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Contact Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'account', fieldLabel: 'Account', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'lastContact', fieldLabel: 'Last Contact', fieldType: 'date', isVisible: true, order: 2 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 3 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', isVisible: true, order: 4 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 5 },
    ],
    sortBy: {
      fieldApiName: 'lastContact',
      direction: 'desc'
    },
    createdBy: 'Admin',
    createdDate: '2024-03-05T11:30:00Z'
  },
  {
    id: 'lv_contacts_active',
    name: 'Active Contacts',
    apiName: 'active_contacts',
    objectApiName: 'Contact',
    description: 'Contacts with Active status',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Contact Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'title', fieldLabel: 'Title', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'account', fieldLabel: 'Account', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 4 },
      { fieldApiName: 'mobile', fieldLabel: 'Mobile', fieldType: 'phone', isVisible: true, order: 5 },
    ],
    filters: [
      { fieldApiName: 'status', operator: 'equals', value: 'Active' }
    ],
    createdBy: 'Admin',
    createdDate: '2024-04-12T14:15:00Z'
  }
];

/**
 * DEFAULT CONTACT RECORDS
 */
export const defaultContactRecords: ListViewRecord[] = [
  {
    id: 'contact_001',
    name: 'Jennifer Taylor',
    firstName: 'Jennifer',
    lastName: 'Taylor',
    fullName: 'Jennifer Taylor',
    title: 'Chief Technology Officer',
    accountId: 'client_004',
    account: 'Tech Innovations Inc',
    email: 'j.taylor@techinnovations.com',
    phone: '(555) 789-0123',
    mobile: '(555) 789-0124',
    status: 'Active',
    owner: 'John Smith',
    department: 'Executive',
    lastContact: '2025-01-15T14:30:00Z',
    createdDate: '2024-03-10T09:00:00Z',
    createdBy: 'user_admin'
  },
  {
    id: 'contact_002',
    name: 'Robert Chen',
    firstName: 'Robert',
    lastName: 'Chen',
    fullName: 'Robert Chen',
    title: 'Operations Manager',
    accountId: 'client_003',
    account: 'Global Manufacturing Corp',
    email: 'r.chen@globalmanuf.com',
    phone: '(555) 890-1234',
    mobile: '(555) 890-1235',
    status: 'Active',
    owner: 'Sarah Johnson',
    department: 'Operations',
    lastContact: '2025-01-14T11:00:00Z',
    createdDate: '2024-04-15T10:30:00Z',
    createdBy: 'user_admin'
  },
  {
    id: 'contact_003',
    name: 'Amanda Foster',
    firstName: 'Amanda',
    lastName: 'Foster',
    fullName: 'Amanda Foster',
    title: 'Marketing Director',
    accountId: 'client_006',
    account: 'Retail Solutions LLC',
    email: 'a.foster@retailsolutions.com',
    phone: '(555) 901-2345',
    mobile: '(555) 901-2346',
    status: 'Active',
    owner: 'Michael Chen',
    department: 'Marketing',
    lastContact: '2025-01-16T09:00:00Z',
    createdDate: '2024-05-20T14:00:00Z',
    createdBy: 'user_michael'
  },
  {
    id: 'contact_004',
    name: 'David Martinez',
    firstName: 'David',
    lastName: 'Martinez',
    fullName: 'David Martinez',
    title: 'Finance Director',
    accountId: 'client_005',
    account: 'Financial Services Group',
    email: 'd.martinez@finservices.com',
    phone: '(555) 012-3456',
    mobile: '(555) 012-3457',
    status: 'Active',
    owner: 'Emily Davis',
    department: 'Finance',
    lastContact: '2025-01-13T16:45:00Z',
    createdDate: '2023-11-05T09:15:00Z',
    createdBy: 'user_admin'
  },
  {
    id: 'contact_005',
    name: 'Lisa Anderson',
    title: 'VP of Sales',
    accountId: 'client_007',
    account: 'Healthcare Innovations',
    email: 'l.anderson@healthcareinnovations.com',
    phone: '(555) 123-4567',
    mobile: '(555) 123-4568',
    status: 'Active',
    owner: 'David Wilson',
    department: 'Sales',
    lastContact: '2025-01-14T10:15:00Z',
    createdDate: '2024-01-08T11:30:00Z'
  },
  {
    id: 'contact_006',
    name: 'James Wilson',
    title: 'Senior Engineer',
    accountId: 'client_004',
    account: 'Tech Startup LLC',
    email: 'j.wilson@techstartup.io',
    phone: '(555) 234-5678',
    mobile: '(555) 234-5679',
    status: 'Active',
    owner: 'Sarah Johnson',
    department: 'Engineering',
    lastContact: '2025-01-12T09:30:00Z',
    createdDate: '2024-06-22T10:00:00Z'
  },
  {
    id: 'contact_007',
    name: 'Patricia Lee',
    title: 'HR Manager',
    accountId: 'client_009',
    account: 'Construction Pro LLC',
    email: 'p.lee@constructionpro.com',
    phone: '(555) 345-6789',
    mobile: '(555) 345-6790',
    status: 'Inactive',
    owner: 'Michael Chen',
    department: 'Human Resources',
    lastContact: '2024-12-15T13:00:00Z',
    createdDate: '2023-09-18T13:30:00Z'
  },
  {
    id: 'contact_008',
    name: 'Christopher Davis',
    title: 'Product Manager',
    accountId: 'client_008',
    account: 'Education Tech Solutions',
    email: 'c.davis@edtechsolutions.com',
    phone: '(555) 456-7890',
    mobile: '(555) 456-7891',
    status: 'Active',
    owner: 'John Smith',
    department: 'Product',
    lastContact: '2025-01-15T15:45:00Z',
    createdDate: '2024-07-30T14:00:00Z'
  },
  {
    id: 'contact_009',
    name: 'Michelle Brown',
    title: 'Customer Success Lead',
    accountId: 'client_001',
    account: 'Software Dynamics',
    email: 'm.brown@softwaredynamics.com',
    phone: '(555) 567-8901',
    mobile: '(555) 567-8902',
    status: 'Active',
    owner: 'Emily Davis',
    department: 'Customer Success',
    lastContact: '2025-01-16T10:00:00Z',
    createdDate: '2024-08-14T09:30:00Z'
  },
  {
    id: 'contact_010',
    name: 'Daniel Thompson',
    title: 'Legal Counsel',
    accountId: 'client_010',
    account: 'Legal Associates',
    email: 'd.thompson@legalassociates.com',
    phone: '(555) 678-9012',
    mobile: '(555) 678-9013',
    status: 'Active',
    owner: 'David Wilson',
    department: 'Legal',
    lastContact: '2025-01-11T16:30:00Z',
    createdDate: '2024-02-25T09:45:00Z'
  },
  {
    id: 'contact_011',
    name: 'Karen Rodriguez',
    title: 'Business Analyst',
    accountId: 'client_003',
    account: 'Global Industries Inc',
    email: 'k.rodriguez@globalind.com',
    phone: '(555) 789-0123',
    mobile: '(555) 789-0124',
    status: 'Active',
    owner: 'Sarah Johnson',
    department: 'Business Intelligence',
    lastContact: '2025-01-10T11:00:00Z',
    createdDate: '2024-09-05T10:15:00Z'
  },
  {
    id: 'contact_012',
    name: 'Thomas Miller',
    title: 'IT Director',
    accountId: 'client_006',
    account: 'Retail Solutions Co',
    email: 't.miller@retailsolutions.com',
    phone: '(555) 890-1234',
    mobile: '(555) 890-1235',
    status: 'Active',
    owner: 'Michael Chen',
    department: 'Information Technology',
    lastContact: '2025-01-13T10:15:00Z',
    createdDate: '2023-12-10T14:00:00Z'
  },
  // Firm (Account) contacts for pricebook managing partner
  { id: 'contact_acc_001_1', name: 'Sarah Mitchell', title: 'Partner', accountId: 'acc_001', account: 'Acme Law Firm', email: 's.mitchell@acmelaw.com', phone: '(555) 111-0001', status: 'Active', owner: 'Admin', lastContact: '2025-01-15T00:00:00Z', createdDate: '2024-01-01T00:00:00Z' },
  { id: 'contact_acc_002_1', name: 'James Cole', title: 'Managing Partner', accountId: 'acc_002', account: 'Global Partners LLC', email: 'j.cole@globalpartners.com', phone: '(555) 222-0002', status: 'Active', owner: 'Admin', lastContact: '2025-01-15T00:00:00Z', createdDate: '2024-01-01T00:00:00Z' },
  { id: 'contact_acc_003_1', name: 'Emily Ross', title: 'Partner', accountId: 'acc_003', account: 'Summit Financial Group', email: 'e.ross@summitfinancial.com', phone: '(555) 333-0003', status: 'Active', owner: 'Admin', lastContact: '2025-01-15T00:00:00Z', createdDate: '2024-01-01T00:00:00Z' },
  { id: 'contact_acc_004_1', name: 'Michael Torres', title: 'Managing Partner', accountId: 'acc_004', account: 'Riverside Legal Associates', email: 'm.torres@riversidelegal.com', phone: '(555) 444-0004', status: 'Active', owner: 'Admin', lastContact: '2025-01-14T00:00:00Z', createdDate: '2024-05-01T00:00:00Z' },
  { id: 'contact_acc_005_1', name: 'Jennifer Walsh', title: 'Partner', accountId: 'acc_005', account: 'Metro Credit Advisors', email: 'j.walsh@metrocredit.com', phone: '(555) 555-0005', status: 'Active', owner: 'Admin', lastContact: '2025-01-13T00:00:00Z', createdDate: '2024-06-01T00:00:00Z' },
  { id: 'contact_acc_006_1', name: 'David Kim', title: 'Senior Partner', accountId: 'acc_006', account: 'Pacific Coast Financial', email: 'd.kim@pacificcoast.com', phone: '(555) 666-0006', status: 'Active', owner: 'Admin', lastContact: '2025-01-12T00:00:00Z', createdDate: '2024-07-01T00:00:00Z' }
];

/**
 * PERSISTED STORAGE
 */
export const persistedContactListViews = createPersistedMockData(
  'crm_contact_list_views',
  defaultContactListViews
);

export const persistedContactRecords = createPersistedMockData(
  'crm_contact_records',
  defaultContactRecords
);

/**
 * BACKWARD COMPATIBILITY
 */
export const mockContactListViews = defaultContactListViews;
export const mockContactRecords = defaultContactRecords;
